import { PropsWithChildren, ReactNode, useMemo } from 'react';

import { Edit } from '@mui/icons-material';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';

import { ACTIVE_STATUSES, PROCESS_STATUS } from 'appConstants';
import { ProcessStatus } from 'appTypes';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlHelperTxt from 'features/common/StlHelperTxt';
import StlTooltip from 'features/common/StlTooltip';
import { displayFriendlyDateWithTime } from 'features/common/datetimeUtils';
import { capitalizeFirstLetter } from 'features/common/utils';
import HexSpinner from 'features/spinner/HexSpinner';

import styles from './StlItemCard.module.css';

interface IStlItemCard<T> {
  fullWidth: boolean;
  item: T;
  selected: boolean;
  img?: ReactNode;
  secondaryLine: string;
  status: ProcessStatus;
  name: string;
  itemType: string;
  setEditMode?: (arg1: T) => void;
  setCopyMode?: (arg1: T) => void;
  menu?: ReactNode;
}

interface WithCreated {
  created_at: string;
  created_by?: string;
}

export default function StlItemCard<T extends WithCreated>({
  item,
  selected,
  fullWidth,
  img,
  secondaryLine,
  children,
  status,
  name,
  itemType,
  setCopyMode,
  setEditMode,
  menu,
}: IStlItemCard<T> & PropsWithChildren) {
  if (!item) return null;

  const user = useAppSelector(selectUser);

  const { created_at, created_by } = item;

  const isOwner = created_by === user?.username;

  const createdByLabel = useMemo(() => {
    if (!created_by) return 'Unknown';
    return isOwner ? 'you' : created_by;
  }, [created_by, isOwner]);

  const showLoading =
    (itemType === 'analysis' || !selected) && ACTIVE_STATUSES.includes(status);

  return (
    <Card
      variant="outlined"
      className={`
        ${selected ? styles.card_selected : styles.card}
        ${fullWidth ? styles.fullWidth : ''}
      `}
    >
      <CardContent className={styles.cardContent} data-stl-card-content>
        {fullWidth && img}
        <Box className={styles.cardTxt}>
          <Box
            className={styles.header}
            sx={selected ? { height: '20px' } : undefined}
          >
            <Box>
              <StlTooltip title={!fullWidth ? name : undefined}>
                <Typography variant="h4" className={styles.subtitle}>
                  {name}
                </Typography>
              </StlTooltip>
              {!selected && (
                <Typography
                  variant="caption"
                  fontSize={'0.75rem'}
                  className={styles.caption}
                >
                  {secondaryLine}
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems={'center'}>
              {showLoading && <HexSpinner size={16} />}
              {status === PROCESS_STATUS.ERROR && (
                <StlHelperTxt
                  variant="error"
                  title={
                    !fullWidth &&
                    `${capitalizeFirstLetter(itemType)} processing failed`
                  }
                  txt={fullWidth && 'Processing failed'}
                />
              )}
              {menu}
            </Stack>
          </Box>

          {children}
          {fullWidth && (
            <Typography variant="caption" className={styles.caption}>
              Added by {createdByLabel} on{' '}
              {displayFriendlyDateWithTime(created_at)}
            </Typography>
          )}
          {selected && !ACTIVE_STATUSES.includes(status) && (
            <Box display="flex" gap={0.5}>
              {setEditMode && (
                <StlButton
                  gray
                  width={116}
                  isActive={isOwner}
                  value="Edit"
                  handleClick={(e) => {
                    e.stopPropagation();
                    setEditMode(item);
                  }}
                  tooltip={
                    !isOwner
                      ? `This ${itemType} was created by another user.\nTo make edits, please create a copy.`
                      : false
                  }
                  icon={
                    <Edit
                      titleAccess={`Edit ${itemType}`}
                      sx={{ fontSize: '1rem !important' }}
                    />
                  }
                />
              )}
              {setCopyMode && (
                <StlButton
                  width={116}
                  gray
                  isActive
                  value="Copy"
                  handleClick={(e) => {
                    e.stopPropagation();
                    setCopyMode(item);
                  }}
                  icon={
                    <FontAwesomeIcon
                      name="copy"
                      title={`Copy ${itemType}`}
                      size="0.75rem"
                    />
                  }
                />
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
